import * as React from "react";

import type { ToastActionElement } from "@/components/ui/toast";
import { closeAllToasts, closeToast, showToast, updateToast, useToastList, type ToastItem } from "@/lib/toastStore";

/**
 * The shadcn-style toast API, drawn by the one staff toast system
 * (src/lib/toastStore.ts, src/components/ui/toaster.tsx).
 */
type Toast = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  variant?: "default" | "destructive" | null;
  /**
   * `Infinity`: stays until closed (a print that did not print). Any other
   * value is ignored: every other toast closes after 5 seconds.
   */
  duration?: number;
  /** At most three of these at once: a fourth closes the oldest. */
  important?: boolean;
};

function input(props: Toast) {
  return {
    title: props.title,
    description: props.description,
    action: props.action,
    tone: props.variant === "destructive" ? ("destructive" as const) : ("default" as const),
    duration: props.duration,
    important: props.important,
  };
}

function toast(props: Toast) {
  const id = showToast({ source: "toast", ...input(props) });
  return {
    id,
    dismiss: () => closeToast(id),
    /** Changes only what `next` names; the countdown keeps running. */
    update: (next: Toast) => {
      const { title, description, action, tone } = input(next);
      updateToast(id, {
        ...("title" in next && { title }),
        ...("description" in next && { description }),
        ...("action" in next && { action }),
        ...("variant" in next && { tone }),
      });
    },
  };
}

function dismiss(toastId?: string) {
  if (toastId === undefined) closeAllToasts("toast");
  else closeToast(toastId);
}

function useToast(): { toasts: readonly ToastItem[]; toast: typeof toast; dismiss: typeof dismiss } {
  const toasts = useToastList();
  return { toasts, toast, dismiss };
}

export { useToast, toast };
